import { useEffect, useState } from 'react';
import { getSupabase, isSupabaseReady } from '@/lib/supabase';
import { signPhotoUrls } from '@/modules/auth/photoUpload';
import { useBlocks } from '@/modules/trust/useBlocks';
import { sanctionsToBannedIds } from '@/modules/trust/moderation';
import { useAuth } from '@/modules/auth/useAuth';
import { useGeolocation } from '@/modules/map/useGeolocation';
import { toDistanceMap, mergeDistances, type NearbyRow } from '@/modules/map/nearbyDistances';
import type { AppProfileRow, HottrUserSanctionRow } from '@/lib/database.types';
import type { MockProfile } from '@/data/mockProfiles';
import { adaptRow, filterBanned } from './adapt';
import { selectLiveCatalogue } from './catalogue';

interface ProfilesState {
  profiles: MockProfile[];
  loading: boolean;
  error: string | null;
  /** True when the data came from Supabase, false when the backend is absent. */
  fromDb: boolean;
}

export const useProfiles = (): ProfilesState => {
  const ready = isSupabaseReady();
  const [state, setState] = useState<ProfilesState>({
    // Empty until live rows arrive. Never seed mock faces — they look live
    // and block commercial trading.
    profiles: [],
    loading: ready,
    error: null,
    fromDb: false,
  });
  const { blockedIds, loading: blocksLoading } = useBlocks();

  // B7 · Real proximity. Only request location for a signed-in user on a real
  // backend — never prompt on public/mock surfaces (useProfiles also powers
  // the always-mounted desktop SideRail). `travelCity` is set when a travel
  // override is shadowing GPS.
  const { user } = useAuth();
  const { coords, travelCity } = useGeolocation(ready && !!user);
  const [distances, setDistances] = useState<Map<string, number> | null>(null);

  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase) return;

    let cancelled = false;

    const load = async () => {
      const { data, error } = await supabase
        .from('app_profiles')
        .select('*')
        .order('last_active', { ascending: false });

      if (cancelled) return;

      if (error) {
        setState({
          profiles: [],
          loading: false,
          error: error.message,
          fromDb: false,
        });
        return;
      }

      const rows = (data ?? []) as AppProfileRow[];
      if (rows.length === 0) {
        setState({
          profiles: [],
          loading: false,
          error: null,
          fromDb: true,
        });
        return;
      }

      // Hide users with active suspended/banned sanctions. RLS keeps
      // sanctions private (self + moderators), so this query returns the
      // intersection of "has any sanction the current user can read" and
      // "rows we just loaded". For ordinary users, that's only their own
      // sanction — irrelevant here. For moderators, full coverage.
      let bannedIds = new Set<string>();
      try {
        const { data: sanctions } = await supabase
          .from('hottr_user_sanctions')
          .select('user_id, sanction')
          .in('sanction', ['suspended', 'banned']);
        bannedIds = sanctionsToBannedIds(
          sanctions as Pick<HottrUserSanctionRow, 'user_id' | 'sanction'>[] | null,
        );
      } catch {
        // table may not exist in older deploys — fail open
      }

      const baseAdapted = filterBanned(rows, bannedIds).map(adaptRow);

      // Mint signed URLs for every storage path referenced across all
      // profiles in one round-trip, then patch them back into the shape.
      const indexMap: { profileIdx: number; photoIdx: number }[] = [];
      const allPaths: string[] = [];
      baseAdapted.forEach((p, profileIdx) => {
        p.photos.forEach((ref, photoIdx) => {
          if (!ref) return;
          indexMap.push({ profileIdx, photoIdx });
          allPaths.push(ref);
        });
      });
      // B1 · UGC media-safety: only surface media that passed moderation
      // (hottr_media.status = 'approved'). RLS already hides non-approved rows
      // from non-owners; this hard-gates the feed so it never references
      // unscanned media. If the table is absent (pre-migration) we don't gate
      // — no regression — but once deployed, unapproved media is dropped.
      let approvedPaths: Set<string> | null = null;
      if (allPaths.length > 0) {
        try {
          const { data: mediaRows, error: mediaErr } = await supabase
            .from('hottr_media')
            .select('path, status')
            .in('path', allPaths);
          if (!mediaErr) {
            approvedPaths = new Set(
              (mediaRows ?? [])
                .filter((m) => (m as { status: string }).status === 'approved')
                .map((m) => (m as { path: string }).path),
            );
          }
        } catch {
          // table missing pre-migration → leave approvedPaths null (no gating)
        }
      }

      const signed = await signPhotoUrls(supabase, allPaths);
      indexMap.forEach(({ profileIdx, photoIdx }, i) => {
        const url = signed[i];
        const approved = approvedPaths ? approvedPaths.has(allPaths[i]) : true;
        baseAdapted[profileIdx].photos[photoIdx] = url && approved ? url : '';
      });
      // Drop any unsigned/unapproved slots so the UI never renders a raw ref.
      baseAdapted.forEach((p) => {
        p.photos = p.photos.filter(Boolean);
      });

      if (cancelled) return;
      setState({
        profiles: selectLiveCatalogue(baseAdapted),
        loading: false,
        error: null,
        fromDb: true,
      });
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  // B7 · Capture position → broadcast it (owner-only) → read fuzzed distances.
  // Exact coordinates never leave hottr_geo; the SECURITY DEFINER RPC returns
  // only bucketed distances. Any failure leaves the synthetic distance in
  // place (fail-open) so the feed never blanks.
  useEffect(() => {
    const supabase = getSupabase();
    if (!supabase || !user || !coords) return;

    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabase as any; // hottr_geo + nearby_profiles aren't in the narrow Database type

    const run = async () => {
      // Broadcast our REAL position so others can find us — but never a
      // travel-preview location: read-only browsing of another city must not
      // write a false physical position into the proximity index.
      if (!travelCity) {
        try {
          await db.from('hottr_geo').upsert(
            {
              user_id: user.id,
              lat: coords.lat,
              lng: coords.lng,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id' },
          );
        } catch {
          // owner-only RLS / table absent → skip; read-side still works
        }
      }

      // Distances to everyone within radius of where we're browsing from
      // (real GPS, or the travel-override city when travelling).
      try {
        const { data, error } = await db.rpc('nearby_profiles', {
          in_lat: coords.lat,
          in_lng: coords.lng,
          radius_km: 50,
        });
        if (cancelled || error) return;
        // Drop our own ~0 km row so we never render "you, nearby".
        setDistances(toDistanceMap(data as NearbyRow[] | null, user.id));
      } catch {
        // RPC missing (pre-migration) → keep synthetic distances
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user?.id, coords?.lat, coords?.lng, travelCity]);

  // Hide blocked profiles from any consumer of useProfiles (feed/grid/map/etc).
  const visible = blocksLoading
    ? state.profiles
    : state.profiles.filter((p) => !p.ownerId || !blockedIds.has(p.ownerId));

  // Overlay real fuzzed distances where we have them; everything else keeps
  // its synthetic distance untouched.
  return { ...state, profiles: mergeDistances(visible, distances) };
};

export const useProfileByHandle = (handle: string) => {
  const { profiles, loading, error, fromDb } = useProfiles();
  const profile = profiles.find((p) => p.handle === handle) ?? null;
  return { profile, loading, error, fromDb };
};
